import React, { useState, useEffect,  useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingCart, Clock, X } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import {db} from '../firebase/config';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {  collection, query, where, onSnapshot } from 'firebase/firestore';
import { getBrandLogo } from '../utils/brandLogos';
import { formatPrice } from '../utils/formatPrice';
import { CHECKOUT_FEE } from '../utils/pricing';
import TrustBar from './TrustBar';
import SiteFooter from './SiteFooter';

const PRIMARY_BUTTON_CLASS = 'bg-gradient-to-r from-red-500 via-orange-500 to-red-500 text-white hover:shadow-lg hover:-translate-y-0.5';
const PRIMARY_BUTTON_DISABLED = 'bg-gray-300 text-gray-500 cursor-not-allowed';
const SECONDARY_BUTTON_CLASS = 'border border-orange-200 text-orange-700 bg-white hover:bg-orange-50 hover:border-orange-300';
const WARM_CARD_CLASS = 'bg-white rounded-2xl border border-orange-100 shadow-[0_18px_45px_rgba(251,146,60,0.12)]';

const PROCESSED_DELIVERY_STATUSES = ['pendingAdminApproval', 'released', 'rejected'];
const PROCESSED_CARD_STATUSES = ['sold', 'rejected'];
const FAILED_PAYMENT_ATTEMPT_STATUSES = ['cancelled', 'failed', 'expired'];

const isProcessedPaymentCard = (card) => (
  PROCESSED_CARD_STATUSES.includes(card.status) ||
  PROCESSED_DELIVERY_STATUSES.includes(card.deliveryStatus)
);

const createPaymentAttemptNonce = (prefix = 'attempt') =>
  `${prefix}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;

const CartManager = {

  getGuestSessionId: () => {
    let guestId = localStorage.getItem('swapify_guest_id');
    if (!guestId) {
      guestId = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('swapify_guest_id', guestId);
    }
    return guestId;
  },

  getCart: () => {
    const cart = localStorage.getItem('swapify_cart');
    return cart ? JSON.parse(cart) : { items: [], expiresAt: null };
  },
  
  setCart: (cartData) => {
    localStorage.setItem('swapify_cart', JSON.stringify(cartData));
  },
  
  addToCart: (giftCard) => {
    const cart = CartManager.getCart();
    
    // Add the specific card as individual item
    const cartItem = {
      ...giftCard,
      cartId: `${giftCard.id}_${Date.now()}`, // Unique cart ID
      addedAt: new Date().toISOString()
    };
    
    cart.items.push(cartItem);
    
    // Reset timer for entire cart when new item added
    cart.expiresAt = new Date(Date.now() + 10 * 60 * 1000).toISOString(); // 10 minutes from now
    
    CartManager.setCart(cart);
    return cart;
  },
  
  removeFromCart: (cartId) => {
    const cart = CartManager.getCart();
    cart.items = cart.items.filter(item => item.cartId !== cartId);
    
    // If cart is empty, clear expiration
    if (cart.items.length === 0) {
      cart.expiresAt = null;
    }
    
    CartManager.setCart(cart);
    return cart;
  },
  
  clearCart: () => {
    localStorage.removeItem('swapify_cart');
  },
  
  isCartExpired: (expiresAt) => {
    if (!expiresAt) return false;
    return new Date() > new Date(expiresAt);
  },
  
  cleanExpiredCart: () => {
    const cart = CartManager.getCart();
    
    if (cart.expiresAt && CartManager.isCartExpired(cart.expiresAt)) {
      // Clear entire cart if expired
      CartManager.clearCart();
      return { items: [], expiresAt: null };
    }
    
    return cart;
  }
};

// Cart Icon Component for Navigation
export const CartIcon = ({ onClick }) => {
  const [cart, setCart] = useState({ items: [], expiresAt: null });
  
  useEffect(() => {
    const updateCart = () => {
      const cleanCart = CartManager.cleanExpiredCart();
      setCart(cleanCart);
    };
    
    updateCart();
    const interval = setInterval(updateCart, 1000); // Update every second
    
    return () => clearInterval(interval);
  }, []);
  
  const totalItems = cart.items.length;
  
  return (
    <button onClick={onClick} className="relative p-2 text-gray-700 hover:text-red-500 transition">
      <ShoppingCart className="w-6 h-6" />
      {totalItems > 0 && (
        <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
          {totalItems}
        </span>
      )}
    </button>
  );
};

// Timer Component
const CartTimer = ({ expiresAt }) => {
  const [timeLeft, setTimeLeft] = useState('');
  
  useEffect(() => {
    if (!expiresAt) return;
    
    const updateTimer = () => {
      const now = new Date();
      const expiry = new Date(expiresAt);
      const diff = expiry - now;
      
      if (diff <= 0) {
        setTimeLeft('Verlopen');
        return;
      }
      
      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeLeft(`${minutes}:${seconds.toString().padStart(2, '0')}`);
    };
    
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    
    return () => clearInterval(interval);
  }, [expiresAt]);
  
  if (!expiresAt || timeLeft === 'Verlopen') {
    return null;
  }
  
  return (
    <div className="flex items-center space-x-2 bg-orange-50 border border-orange-200 rounded-lg p-3">
      <Clock className="w-5 h-5 text-orange-600" />
      <div>
        <p className="text-sm font-semibold text-orange-900">Je winkelwagen verloopt over</p>
        <p className="text-2xl font-bold text-orange-600">{timeLeft}</p>
      </div>
    </div>
  );
};

// Cart Step Component
const CartStep = ({ onNext }) => {
  const [cart, setCart] = useState({ items: [], expiresAt: null });
  
  // 1. Initialize all user-related state from the current user
  const [user, setUser] = useState(getAuth().currentUser);
  const [email, setEmail] = useState(getAuth().currentUser ? getAuth().currentUser.email : '');
  const [isProcessing, setIsProcessing] = useState(false); 
  const hasSubmitted = useRef(false);
  const isLoggedIn = !!user;
  const [oppTermsAccepted, setOppTermsAccepted] = useState(false);

  // 2. This effect listens for auth changes...
  useEffect(() => {
    const unsubscribe = getAuth().onAuthStateChanged((user) => {
      setUser(user); // <-- Updates the user state
      
      // 3. ...and ALSO updates the email state at the same time.
      if (user) {
        setEmail(user.email);
      } else {
        setEmail('');
      }
    });
    return () => unsubscribe(); // Clean up listener
  }, []);
  
  // 4. This effect for the cart is fine, no change needed.
  useEffect(() => {
    const loadCart = () => {
      const cleanCart = CartManager.cleanExpiredCart();
         // Migrate old cart items that don't have logos
      const migratedItems = cleanCart.items.map(item => {
        if (!item.logo && item.brand) {
        // Regenerate logo URL for items that don't have it
          return {
            ...item,
            logo: getBrandLogo(item.brand)
          };
        }
        return item;
      });

      if (migratedItems.some((item, i) => item !== cleanCart.items[i].logo)) {
        // Update cart if we migrated any items
        CartManager.setCart({ ...cleanCart, items: migratedItems });
        setCart({ ...cleanCart, items: migratedItems });
      } else {
        setCart(cleanCart);
      }
    };
    
    loadCart();
    const interval = setInterval(loadCart, 1000);
    
    return () => clearInterval(interval);
  }, []);

    
  const handleRemove = async (cartId, cardDocId) => {
  try {
    // Release reservation in backend
    const functions = getFunctions(undefined, 'europe-west1');
    const releaseCard = httpsCallable(functions, 'releaseCard');
    
     // Get guest session ID if not logged in
    const auth = getAuth();
    const payload = { cardId: cardDocId };
    if (!auth.currentUser) {
      const guestSessionId = localStorage.getItem('swapify_guest_id');
      if (guestSessionId) {
        payload.guestSessionId = guestSessionId;
      }
    }
    
    await releaseCard(payload);
    
    
    // Remove from localStorage
    const updatedCart = CartManager.removeFromCart(cartId);
    setCart(updatedCart);
    
  } catch (error) {
    console.error("Error removing from cart:", error);
    
    // Even if backend fails, still remove from localStorage
    // (Card might have already been released by scheduler)
    const updatedCart = CartManager.removeFromCart(cartId);
    setCart(updatedCart);
    
    // Only show error for permission issues
    if (error.code === "functions/permission-denied") {
      alert("Kon cadeaubon niet verwijderen");
    }
  }
};
  
  const calculateSubtotal = () => {
    return cart.items.reduce((sum, item) => sum + (item.price || 0), 0);
  };

  const calculateOriginalTotal = () => {
    return cart.items.reduce((sum, item) => sum + (item.value || 0), 0);
  };

  const calculateCheckoutFee = () => {
    return cart.items.length > 0 ? CHECKOUT_FEE : 0;
  };

  const calculateTotal = () => {
    return calculateSubtotal() + calculateCheckoutFee();
  };
  
  const calculateSavings = () => {
    return calculateOriginalTotal() - calculateTotal();
  };
  
  const handleNext = () => {
    if (isProcessing || hasSubmitted.current) return; 

    setIsProcessing(true); 
    hasSubmitted.current = true;

    const subtotal = calculateSubtotal();
    const checkoutFee = calculateCheckoutFee();
    const total = calculateTotal();
    const savings = calculateSavings();

    const guestSessionId = CartManager.getGuestSessionId();
    const hasPreviousCheckoutAttempt = Boolean(
      localStorage.getItem('last_checkout_attempt_id') ||
      localStorage.getItem('last_mtr_uid')
    );
    
    onNext({
      cart: cart.items,
      subtotal,
      checkoutFee,
      total: total,
      savings: savings,
      email: email || null,
      guestSessionId: guestSessionId,
      orderId: `ord_${Date.now()}`,
      paymentAttemptNonce: hasPreviousCheckoutAttempt ? createPaymentAttemptNonce('retry') : 'initial'
    });
  };
  
  if (cart.items.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center py-16">
        <ShoppingCart className="w-24 h-24 mx-auto text-gray-300 mb-6" />
        <h2 className="text-2xl font-semibold text-gray-900 mb-4">Je winkelwagen is leeg</h2>
        <p className="text-gray-600 mb-8">Tijd om te shoppen! Bekijk de marktplaats voor cadeaukaarten met korting.</p>
        <Link 
          to="/marketplace"
          className={`inline-block px-8 py-4 rounded-xl font-semibold transition ${PRIMARY_BUTTON_CLASS}`}
        >
          Bekijk marktplaats
        </Link>
      </div>
    );
  }
  
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-4xl font-semibold text-gray-900 mb-3">Winkelwagen</h1>
        <p className="text-lg text-gray-600">Even samen checken, daarna sturen we je door naar een veilige betaling via iDEAL.</p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Cart Items */}
        <div className="lg:col-span-2 space-y-4">
          {cart.expiresAt && (
            <CartTimer expiresAt={cart.expiresAt} />
          )}
          
          {cart.items.map((item) => (
            <div key={item.cartId} className={`${WARM_CARD_CLASS} p-6 flex items-center space-x-6`}>
              <div className="w-20 h-20 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <img 
                    src={getBrandLogo(item.brand)} 
                    alt={item.brand}
                    width="80"
                    height="80"
                    className="w-full h-full object-contain p-2"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = `<span class="text-2xl font-semibold text-gray-900">${item.brand.charAt(0)}</span>`;
                    }}
                  />
                </div>
           
              <div className="flex-1">
                  <h3 className="text-xl font-semibold text-gray-900">{item.brand}</h3>
                  <p className="text-gray-600">Waarde: {formatPrice(item.value)}</p>
                  <div className="flex items-center space-x-2 mt-2">
                    <span className="text-2xl font-semibold text-green-600">
                      {formatPrice(item.price)}
                    </span>
                    <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-sm font-semibold">
                      {item.discount}% korting
                    </span>
                  </div>
                </div>
                
                <button
                  onClick={() => handleRemove(item.cartId, item.id)}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            ))}

          <div className="lg:hidden">
            <TrustBar variant="two-column" />
          </div>
        </div>
        
        {/* Summary Sidebar */}
        <div className="lg:col-span-1">
          <div className={`${WARM_CARD_CLASS} p-6 sticky top-4`}>
            <h3 className="text-xl font-semibold text-gray-900 mb-6">Overzicht</h3>
            
            <div className="space-y-3 text-gray-700 mb-6">
              <div className="flex justify-between">
                <span>Subtotaal ({cart.items.length} items)</span>
                <span>{formatPrice(calculateSubtotal())}</span>
              </div>
              <div className="flex justify-between">
                <span>iDEAL kosten (per checkout)</span>
                <span>{formatPrice(calculateCheckoutFee())}</span>
              </div>
              <div className="flex justify-between text-green-600 font-semibold">
                <span>Je bespaart</span>
                <span>{formatPrice(calculateSavings())}</span>
              </div>
              <div className="border-t border-gray-200 pt-3 flex justify-between text-xl font-semibold text-gray-900">
                <span>Totaal</span>
                <span>{formatPrice(calculateTotal())}</span>
              </div>
            </div>
            
            {!isLoggedIn && (
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Email voor bevestiging
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jouw@email.nl"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-orange-200 focus:border-orange-300"
                />
              </div>
            )}

            {/* OPP Terms */}
            <label className="flex items-start space-x-3 mb-4 cursor-pointer">
              <input
                type="checkbox"
                checked={oppTermsAccepted}
                onChange={(e) => setOppTermsAccepted(e.target.checked)}
                className="mt-1 w-4 h-4 accent-orange-500"
              />
              <span className="text-sm text-gray-600">
                Ik ga akkoord met de betaalvoorwaarden van{' '}
                <a href="https://onlinepaymentplatform.com/terms-policies" target="_blank" rel="noopener noreferrer" className="text-orange-700 hover:underline font-semibold">
                  Online Payment Platform
                </a>
                {', onze betaalpartner.'}
              </span>
            </label>
            
            <div className="space-y-3">
              <button
                onClick={handleNext}
                disabled={cart.items.length === 0 || (!isLoggedIn && !email.trim()) || !oppTermsAccepted}
                className={`w-full py-4 rounded-xl font-bold text-lg transition ${
                  cart.items.length > 0 && (isLoggedIn || email.trim()) && !isProcessing 
                    ? PRIMARY_BUTTON_CLASS
                    : PRIMARY_BUTTON_DISABLED
                }`}
              >
                {isProcessing ? 'Bezig...' : 'Doorgaan naar betaling'} {/* ✅ Show loading text */}

              </button>
              
              <Link 
                to="/marketplace"
                className="block w-full py-3 border rounded-xl font-semibold text-center transition border-gray-300 text-gray-700 hover:border-orange-300 hover:text-orange-700 hover:bg-orange-50"
              >
                Verder winkelen
              </Link>
            </div>
            
            <div className="hidden lg:block mt-6">
              <TrustBar variant="compact" />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PaymentStep = ({ cartData, onBack }) => {
  const [error, setError] = useState(null);
  const paymentInitiated = useRef(false); //Persistent guard to prevent multiple initiations

  const createPayment = async () => {
    if (paymentInitiated.current) return;
    paymentInitiated.current = true;
    try {
      setError(null);
      // Ensure we target the correct region
      const functionsInstance = getFunctions(undefined, 'europe-west1');
      const createOPPPayment = httpsCallable(functionsInstance, 'createOPPPayment');

      const result = await createOPPPayment({
        cartItems: cartData.cart,
        buyerEmail: cartData.email,
        guestSessionId: cartData.guestSessionId,
        paymentAttemptNonce: cartData.paymentAttemptNonce || 'initial'
      });

      // Extract our new keys from the Cloud Function response
      const { redirectUrl, transactionUid, paymentId, checkoutAttemptId, paymentStatus } = result.data;
      const mtr = transactionUid || paymentId; 

      if (mtr) {
        localStorage.setItem("last_mtr_uid", mtr);
        if (checkoutAttemptId) {
          localStorage.setItem("last_checkout_attempt_id", checkoutAttemptId);
        }
        localStorage.setItem("last_mtr_created_at", String(Date.now()));
        localStorage.setItem("last_checkout_summary", JSON.stringify({
          paymentId: mtr,
          checkoutAttemptId: checkoutAttemptId || null,
          buyerEmail: cartData.email || null,
          guestSessionId: cartData.guestSessionId || null,
          subtotal: cartData.subtotal || 0,
          checkoutFee: cartData.checkoutFee || 0,
          total: cartData.total || 0,
          items: cartData.cart.map(item => ({
            brand: item.brand,
            value: item.value,
            price: item.price,
            discount: item.discount
          }))
        }));
        if (FAILED_PAYMENT_ATTEMPT_STATUSES.includes(paymentStatus)) {
          window.location.href = `/buy/failed${checkoutAttemptId ? `?attempt_id=${encodeURIComponent(checkoutAttemptId)}` : ''}`;
          return;
        }

        if (redirectUrl) {
          window.location.href = redirectUrl;
          return;
        }
      }

      throw new Error('Geen geldige redirect data ontvangen van de server.');
    } catch (err) {
      console.error('❌ Payment Step Error:', err);
      setError(err.message || 'Kon betaling niet aanmaken');
    }
  };

  useEffect(() => {
    createPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4">
        <div className={`${WARM_CARD_CLASS} p-8 text-center`}>
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 text-2xl font-semibold">X</span>
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-3">Er ging iets mis</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex gap-4">
            <button onClick={onBack} className="flex-1 py-4 px-6 border border-gray-300 rounded-xl font-semibold text-gray-700 hover:border-orange-300 hover:text-orange-700 hover:bg-orange-50 transition">
              Terug
            </button>
            <button onClick={createPayment} className={`flex-1 py-4 px-6 rounded-xl font-semibold transition ${PRIMARY_BUTTON_CLASS}`}>
              Opnieuw
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4">
      <div className={`${WARM_CARD_CLASS} p-8 text-center`}>
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-orange-500 mx-auto mb-6"></div>
        <h2 className="text-2xl font-semibold text-gray-900 mb-3">Betaling voorbereiden...</h2>
        <p className="text-gray-600 mb-6">Je wordt nu doorgestuurd naar de veilige betaalomgeving.</p>
        
        <div className="bg-gray-50 rounded-xl p-6 text-left border border-gray-100">
          <h3 className="font-semibold text-gray-900 mb-4">Overzicht</h3>
          <div className="space-y-2 text-sm">
            {cartData.cart.map((item) => (
              <div key={item.cartId} className="flex justify-between">
                <span>{item.brand}</span>
                <span className="font-medium">{formatPrice(item.price)}</span>
              </div>
            ))}
            <div className="flex justify-between">
              <span>iDEAL kosten (per checkout)</span>
              <span className="font-medium">{formatPrice(cartData.checkoutFee || 0)}</span>
            </div>
            <div className="border-t border-gray-200 pt-2 flex justify-between font-semibold text-lg text-gray-900">
              <span>Totaal</span>
              <span>{formatPrice(cartData.total || 0)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const PaymentCheckingState = ({ onCheckAgain, isChecking = false, message }) => (
  <div className="max-w-2xl mx-auto px-4 py-16 text-center">
    <div className={`${WARM_CARD_CLASS} p-8`}>
      <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
        <Clock className="w-8 h-8 text-yellow-600" />
      </div>
      <h2 className="text-2xl font-semibold text-gray-900 mb-4">Betaling wordt gecontroleerd</h2>
      <p className="text-gray-600 mb-6">
        {message || 'We hebben nog geen definitieve bevestiging ontvangen. Als je net hebt betaald, komt dit meestal vanzelf binnen.'}
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        {onCheckAgain && (
          <button
            onClick={onCheckAgain}
            disabled={isChecking}
            className={`flex-1 px-6 py-3 rounded-xl font-semibold transition ${isChecking ? PRIMARY_BUTTON_DISABLED : PRIMARY_BUTTON_CLASS}`}
          >
            {isChecking ? 'Controleren...' : 'Opnieuw controleren'}
          </button>
        )}
        <Link to="/buy" className={`flex-1 px-6 py-3 rounded-xl font-semibold transition ${SECONDARY_BUTTON_CLASS}`}>
          Terug naar betaling
        </Link>
      </div>
    </div>
  </div>
);

const getStoredCheckoutSummary = () => {
  try {
    return JSON.parse(localStorage.getItem('last_checkout_summary') || 'null');
  } catch (storageError) {
    console.error('Could not parse checkout summary:', storageError);
    return null;
  }
};

const PaymentReturnStep = () => {
  const navigate = useNavigate();
  const [authResolved, setAuthResolved] = useState(!!getAuth().currentUser);
  const [error, setError] = useState(null);
  const [isChecking, setIsChecking] = useState(true);
  const [pollKey, setPollKey] = useState(0);
  const checkoutSummaryRef = useRef(null);

  useEffect(() => {
    checkoutSummaryRef.current = getStoredCheckoutSummary();
  }, []);

  useEffect(() => {
    const unsubscribeAuth = getAuth().onAuthStateChanged(() => {
      setAuthResolved(true);
    });

    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    if (!authResolved) return;

    let cancelled = false;
    let pollTimer = null;
    let attempts = 0;
    const urlParams = new URLSearchParams(window.location.search);
    const checkoutSummary = checkoutSummaryRef.current;
    const checkoutAttemptId =
      urlParams.get('attempt_id') ||
      checkoutSummary?.checkoutAttemptId ||
      localStorage.getItem('last_checkout_attempt_id');
    const paymentId =
      urlParams.get('payment_id') ||
      urlParams.get('uid') ||
      urlParams.get('transaction_uid') ||
      urlParams.get('multi_transaction_uid') ||
      checkoutSummary?.paymentId ||
      localStorage.getItem('last_mtr_uid');
    const guestSessionId = checkoutSummary?.guestSessionId || localStorage.getItem('swapify_guest_id') || undefined;

    if (!checkoutAttemptId && !paymentId) {
      setError('We kunnen deze betaalpoging niet vinden. Probeer de betaling opnieuw vanuit je winkelwagen.');
      setIsChecking(false);
      return undefined;
    }

    const routeToFailed = (targetAttemptId) => {
      const params = new URLSearchParams();
      if (targetAttemptId) {
        params.set('attempt_id', targetAttemptId);
      }
      navigate(`/buy/failed${params.toString() ? `?${params.toString()}` : ''}`, { replace: true });
    };

    const routeToSuccess = (targetAttemptId, targetPaymentId) => {
      const params = new URLSearchParams({ step: 'success' });
      if (targetAttemptId) {
        params.set('attempt_id', targetAttemptId);
      }
      if (targetPaymentId) {
        params.set('payment_id', targetPaymentId);
      }
      navigate(`/buy?${params.toString()}`, { replace: true });
    };

    const checkStatus = async () => {
      attempts += 1;
      setIsChecking(true);
      setError(null);

      try {
        const functionsInstance = getFunctions(undefined, 'europe-west1');
        const getPaymentAttemptStatus = httpsCallable(functionsInstance, 'getPaymentAttemptStatus');
        const result = await getPaymentAttemptStatus({
          checkoutAttemptId,
          paymentId,
          guestSessionId,
        });
        if (cancelled) {
          return;
        }
        const attemptStatus = result.data || {};
        const status = attemptStatus.status || 'unknown';
        const targetAttemptId = attemptStatus.checkoutAttemptId || checkoutAttemptId;
        const targetPaymentId = attemptStatus.paymentId || paymentId;

        if (FAILED_PAYMENT_ATTEMPT_STATUSES.includes(status)) {
          routeToFailed(targetAttemptId);
          return;
        }

        if (status === 'completed') {
          routeToSuccess(targetAttemptId, targetPaymentId);
          return;
        }
      } catch (statusError) {
        console.error('Payment return status check failed:', statusError);
        if (statusError.code === 'functions/permission-denied') {
          setError('Deze betaalpoging hoort niet bij jouw sessie. Log in met hetzelfde account of probeer opnieuw.');
          setIsChecking(false);
          return;
        }
      }

      if (!cancelled && attempts < 15) {
        pollTimer = window.setTimeout(checkStatus, 2000);
      } else if (!cancelled) {
        setIsChecking(false);
      }
    };

    checkStatus();

    return () => {
      cancelled = true;
      if (pollTimer) {
        window.clearTimeout(pollTimer);
      }
    };
  }, [authResolved, navigate, pollKey]);

  const checkAgain = () => {
    setError(null);
    setIsChecking(true);
    setPollKey((key) => key + 1);
  };

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <div className={`${WARM_CARD_CLASS} p-8`}>
          <div className="text-red-500 text-6xl mb-4">!</div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Betaalstatus onbekend</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={checkAgain} className={`flex-1 px-6 py-3 rounded-xl font-semibold transition ${PRIMARY_BUTTON_CLASS}`}>
              Opnieuw controleren
            </button>
            <Link to="/buy" className={`flex-1 px-6 py-3 rounded-xl font-semibold transition ${SECONDARY_BUTTON_CLASS}`}>
              Terug naar betaling
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <PaymentCheckingState
      onCheckAgain={checkAgain}
      isChecking={isChecking}
      message={isChecking
        ? 'We controleren bij OPP of de betaling is gelukt. Je komt vanzelf op de juiste pagina zodra de status binnen is.'
        : 'We hebben nog geen definitieve bevestiging ontvangen. Als je net hebt betaald, komt dit meestal vanzelf binnen.'}
    />
  );
};

// ============================================================================
// SUCCESS STEP COMPONENT
// ============================================================================
const SuccessStep = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [settling, setSettling] = useState(false);
  const [copied, setCopied] = useState({});
  const [currentUser, setCurrentUser] = useState(getAuth().currentUser);
  const [authResolved, setAuthResolved] = useState(!!getAuth().currentUser);
  const isAuthenticated = !!currentUser;
  const isInitialMount = useRef(true);
  const checkoutSummaryRef = useRef(null);

  useEffect(() => {
    const unsubscribeAuth = getAuth().onAuthStateChanged((user) => {
      setCurrentUser(user);
      setAuthResolved(true);
    });

    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    if (!authResolved) return;

    // Only run the setup once
    if (!isInitialMount.current) return;
    isInitialMount.current = false;

    const urlParams = new URLSearchParams(window.location.search);
    const checkoutAttemptId = urlParams.get('attempt_id') || localStorage.getItem("last_checkout_attempt_id");
    // Universal ID grabber
    let paymentId = urlParams.get('multi_transaction_uid') || 
                      urlParams.get('payment_id') ||
                      urlParams.get('uid') || 
                      urlParams.get('transaction_uid')||
                      localStorage.getItem("last_mtr_uid");

    // If it's the literal placeholder, ignore it
    if (paymentId && paymentId.includes('{')) {
      paymentId = null;
    }
    
    // Fallback to localStorage
    paymentId = paymentId || localStorage.getItem("last_mtr_uid");

    if (paymentId && !urlParams.get('payment_id')) {
      const nextUrl = new URL(window.location.href);
      nextUrl.searchParams.set('step', 'success');
      nextUrl.searchParams.set('payment_id', paymentId);
      window.history.replaceState(null, '', `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
    }

    try {
      const checkoutSummary = JSON.parse(localStorage.getItem('last_checkout_summary') || 'null');
      if (checkoutSummary && (!paymentId || checkoutSummary.paymentId === paymentId)) {
        checkoutSummaryRef.current = checkoutSummary;
        paymentId = paymentId || checkoutSummary.paymentId;
      }
    } catch (storageError) {
      console.error('Could not parse checkout summary:', storageError);
    }

    const checkAttemptStatus = async () => {
      if (!checkoutAttemptId && !paymentId) return null;

      const functionsInstance = getFunctions(undefined, 'europe-west1');
      const getPaymentAttemptStatus = httpsCallable(functionsInstance, 'getPaymentAttemptStatus');
      const result = await getPaymentAttemptStatus({
        checkoutAttemptId,
        paymentId,
        guestSessionId: checkoutSummaryRef.current?.guestSessionId || localStorage.getItem('swapify_guest_id') || undefined,
      });
      return result.data;
    };

    checkAttemptStatus()
      .then((attemptStatus) => {
        if (!attemptStatus) return;
        if (FAILED_PAYMENT_ATTEMPT_STATUSES.includes(attemptStatus.status)) {
          const targetAttemptId = attemptStatus.checkoutAttemptId || checkoutAttemptId || '';
          window.location.href = `/buy/failed${targetAttemptId ? `?attempt_id=${encodeURIComponent(targetAttemptId)}` : ''}`;
          return;
        }

        if (!isAuthenticated && attemptStatus.status === 'completed') {
          CartManager.clearCart();
          localStorage.removeItem("last_mtr_uid");
          localStorage.removeItem("last_checkout_attempt_id");
          localStorage.removeItem("last_mtr_created_at");
          localStorage.removeItem("last_checkout_summary");
          setSettling(false);
          setLoading(false);
        }
      })
      .catch((statusError) => {
        console.error('Payment status check failed:', statusError);
      });

    if (!isAuthenticated) {
      setSettling(true);
      setLoading(false);
      return;
    }

    if (!paymentId) {
      setError('Betaling gevonden, maar ID ontbreekt in de URL.');
      setLoading(false);
      return;
    }

// Use a local variable for the timer to avoid dependency issues
    const safetyTimer = setTimeout(() => {
      setLoading(currentLoading => {
        setTransactions(currentTransactions => {
          if (currentLoading && currentTransactions.length === 0) {
            setSettling(true);
          }
          return currentTransactions;
        });
        return false;
      });
    }, 15000);

    const q = query(
      collection(db, 'giftcards'),
      where('paymentId', '==', paymentId)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        const cardData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
          
        }));
        const processedCards = cardData.filter(isProcessedPaymentCard);

        if (processedCards.length === 0) {
          setError(null);
          return;
        }

        setTransactions(processedCards);
        setSettling(false);
        setLoading(false);
        clearTimeout(safetyTimer);
        CartManager.clearCart();
        localStorage.removeItem("last_mtr_uid");
        localStorage.removeItem("last_checkout_attempt_id");
        localStorage.removeItem("last_mtr_created_at");
        localStorage.removeItem("last_checkout_summary");
      }
    }, (err) => {
      console.error('Firestore Error:', err);
      if (err.code === 'permission-denied') {
        setSettling(true);
      } else {
        setError('Er is een probleem met het ophalen van de codes.');
      }
      setLoading(false);
    });

    return () => {
      unsubscribe();
      clearTimeout(safetyTimer);
    };
  }, [authResolved, isAuthenticated]);

  const copyToClipboard = (text, field) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopied({ ...copied, [field]: true });
      setTimeout(() => setCopied(prev => ({ ...prev, [field]: false })), 2000);
    });
  };

  const checkAgain = () => {
    setLoading(true);
    setSettling(false);
    window.location.reload();
  };

  const hasReleasedCodes = transactions.some((card) =>
    card.deliveryStatus === 'released' || (card.status === 'sold' && card.revealedCodes?.number)
  );
  const hasPendingVerification = transactions.some((card) =>
    card.deliveryStatus === 'pendingAdminApproval' || (!card.revealedCodes?.number && card.status === 'sold')
  );
  const hasRejectedDelivery = transactions.some((card) => card.deliveryStatus === 'rejected' || card.status === 'rejected');
  const checkoutFee = Number(
    transactions.find((card) => card.checkoutFee != null)?.checkoutFee ??
    checkoutSummaryRef.current?.checkoutFee ??
    0,
  );
  const subtotal = Number(
    checkoutSummaryRef.current?.subtotal ??
    transactions.reduce((sum, card) => sum + Number(card.sellingPrice || 0), 0),
  );
  const buyerTotal = Number(
    transactions.find((card) => card.buyerTotal != null)?.buyerTotal ??
    checkoutSummaryRef.current?.total ??
    (subtotal + checkoutFee),
  );
  const guestSummaryItems = checkoutSummaryRef.current?.items || [];

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto py-16 text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-orange-500 mx-auto mb-6"></div>
        <h2 className="text-2xl font-semibold text-gray-900">Even checken…</h2>
        <p className="text-gray-600">We controleren je betaling en zetten je codes alvast klaar.</p>
      </div>
    );
  }

  if (settling) {
    return (
      <PaymentCheckingState
        onCheckAgain={checkAgain}
        message="We hebben nog geen definitieve bevestiging ontvangen. Als je net hebt betaald, komt dit meestal vanzelf binnen. Heb je de betaling afgebroken of geannuleerd, dan kun je opnieuw proberen."
      />
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16 text-center">
        <div className={`${WARM_CARD_CLASS} p-8`}>
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">Er ging iets mis</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6 text-left">
            <p className="text-sm text-yellow-900 mb-2"><strong>Geen paniek.</strong> We konden de betaalstatus nog niet ophalen.</p>
            <ul className="text-sm text-yellow-800 space-y-1">
              <li>• Ververs deze pagina over een moment.</li>
              <li>• Neem contact op via hello@swapify.nl bij vragen.</li>
            </ul>
          </div>
          <Link to="/marketplace" className={`inline-block px-8 py-3 rounded-xl font-semibold transition ${PRIMARY_BUTTON_CLASS}`}>
            Terug naar marktplaats
          </Link>
        </div>
      </div>
    );
  }

  if (!isAuthenticated && !loading && transactions.length === 0 && !error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 bg-orange-100">
            <svg className="w-10 h-10 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl sm:text-4xl font-semibold text-gray-900 mb-3">Betaling ontvangen!</h1>
          <p className="text-sm sm:text-lg text-gray-600">
            Top, je betaling is gelukt. Je ontvangt je cadeauboncode per e-mail zodra deze is geverifieerd.
          </p>
        </div>

        {checkoutSummaryRef.current && (
          <div className={`${WARM_CARD_CLASS} p-4 sm:p-6 mb-8`}>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Betaaloverzicht</h2>
            <div className="space-y-3 text-sm sm:text-base text-gray-700">
              {guestSummaryItems.length > 0 && (
                <>
                  <div className="space-y-3">
                    {guestSummaryItems.map((item, index) => (
                      <div key={`${item.brand}-${item.value}-${item.price}-${index}`} className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900">{item.brand}</p>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            <span className="text-sm text-gray-500">Waarde {formatPrice(item.value)}</span>
                            {item.discount != null && (
                              <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-semibold">
                                {item.discount}% korting
                              </span>
                            )}
                          </div>
                        </div>
                        <span className="font-semibold text-gray-900 whitespace-nowrap">{formatPrice(item.price)}</span>
                      </div>
                    ))}
                  </div>
                  <div className="border-t border-gray-200 pt-3" />
                </>
              )}
              <div className="flex justify-between">
                <span>Subtotaal cadeaubonnen</span>
                <span>{formatPrice(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span>iDEAL kosten (per checkout)</span>
                <span>{formatPrice(checkoutFee)}</span>
              </div>
              <div className="flex justify-between border-t border-gray-200 pt-3 font-semibold text-gray-900">
                <span>Totaal betaald</span>
                <span>{formatPrice(buyerTotal)}</span>
              </div>
            </div>
          </div>
        )}

        <div className={`${WARM_CARD_CLASS} p-5 sm:p-6 mb-8`}>
          <h2 className="text-lg font-semibold text-gray-900 mb-5">Hoe gaat het verder?</h2>
          <div className="space-y-4">
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-orange-100 text-orange-700 font-semibold flex items-center justify-center flex-shrink-0">1</div>
              <div>
                <h3 className="font-semibold text-gray-900">Wij controleren de cadeaubon</h3>
                <p className="text-sm text-gray-600 mt-1">We checken of het saldo klopt. Dit duurt meestal minder dan 24 uur.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-orange-100 text-orange-700 font-semibold flex items-center justify-center flex-shrink-0">2</div>
              <div>
                <h3 className="font-semibold text-gray-900">Code per e-mail</h3>
                <p className="text-sm text-gray-600 mt-1">Zodra alles in orde is, ontvang je de code direct in je inbox. Check ook je spam.</p>
              </div>
            </div>
            <div className="flex items-start gap-4">
              <div className="w-8 h-8 rounded-full bg-orange-100 text-orange-700 font-semibold flex items-center justify-center flex-shrink-0">3</div>
              <div>
                <h3 className="font-semibold text-gray-900">Werkt het niet? Wij helpen</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Meld het binnen 12 uur via <a href="mailto:hello@swapify.nl" className="text-orange-700 font-medium hover:text-orange-800">hello@swapify.nl</a> en we lossen het op.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 sm:p-6 mb-8 flex items-start sm:items-center space-x-3 sm:space-x-4">
          <div className="text-orange-600 text-2xl">📧</div>
          <p className="text-sm text-orange-900">
            Geen e-mail ontvangen na 24 uur? Neem contact op via <a href="mailto:hello@swapify.nl" className="font-semibold underline decoration-orange-300 underline-offset-2 hover:text-orange-800">hello@swapify.nl</a> — we zoeken het voor je uit.
          </p>
        </div>

        <Link to="/marketplace" className={`block text-center px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold text-sm sm:text-base transition ${PRIMARY_BUTTON_CLASS}`}>
          Terug naar marktplaats
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${
          hasPendingVerification && !hasReleasedCodes ? 'bg-yellow-100' : 'bg-orange-100'
        }`}>
          {hasPendingVerification && !hasReleasedCodes ? (
            <svg className="w-10 h-10 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ) : (
            <svg className="w-10 h-10 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          )}
        </div>
        <h1 className="text-2xl sm:text-4xl font-semibold text-gray-900 mb-3">
          {hasRejectedDelivery && !hasReleasedCodes && !hasPendingVerification
            ? 'Bestelling afgewezen'
            : hasPendingVerification && !hasReleasedCodes
            ? 'Betaling ontvangen'
            : 'Yes! Gelukt 🎉'}
        </h1>
        <p className="text-sm sm:text-lg text-gray-600">
          {hasRejectedDelivery && !hasReleasedCodes && !hasPendingVerification
            ? 'Deze bestelling kon niet worden verwerkt. Een terugbetaling wordt voor je klaargezet.'
            : hasPendingVerification && !hasReleasedCodes
            ? 'Je betaling is ontvangen. De cadeauboncode wordt na verificatie per e-mail naar je verstuurd.'
            : hasPendingVerification
            ? 'Een deel van je bestelling is direct beschikbaar. Eventuele overige cadeauboncodes volgen na verificatie per e-mail.'
            : 'Je betaling is verwerkt en je cadeaubon is direct beschikbaar. Lekker bezig.'}
        </p>
      </div>

      {transactions.length > 0 && (
        <div className={`${WARM_CARD_CLASS} p-4 sm:p-6 mb-8`}>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Betaaloverzicht</h2>
          <div className="space-y-3 text-sm sm:text-base text-gray-700">
            <div className="flex justify-between">
              <span>Subtotaal cadeaubonnen</span>
              <span>{formatPrice(subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span>iDEAL kosten (per checkout)</span>
              <span>{formatPrice(checkoutFee)}</span>
            </div>
            <div className="flex justify-between border-t border-gray-200 pt-3 font-semibold text-gray-900">
              <span>Totaal betaald</span>
              <span>{formatPrice(buyerTotal)}</span>
            </div>
          </div>
        </div>
      )}
    
      {transactions.map((card) => (
        <div
          key={card.id}
          className={`bg-white rounded-2xl shadow-lg p-4 sm:p-8 mb-6 border ${
            card.deliveryStatus === 'pendingAdminApproval' || !card.revealedCodes?.number
              ? 'border-yellow-100'
              : 'border-orange-100'
          }`}
        >
          {card.deliveryStatus === 'rejected' || card.status === 'rejected' ? (
            <>
              <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-6 gap-2">
                <div>
                  <h3 className="text-lg sm:text-2xl font-semibold text-gray-900 mb-1">
                    {card.brand || "Cadeaubon"}
                  </h3>
                  <p className="text-red-700 font-semibold text-sm">Bestelling afgewezen</p>
                </div>
                <div className="sm:text-right">
                  <span className="block text-sm text-gray-400">Waarde: {formatPrice(card.originalValue)}</span>
                </div>
              </div>
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <p className="text-sm text-red-900 font-semibold">Terugbetaling volgt</p>
                <p className="text-sm text-red-800 mt-1">
                  Deze cadeaubon kon niet worden geverifieerd. Je ontvangt een volledige terugbetaling.
                </p>
              </div>
            </>
          ) : card.deliveryStatus === 'pendingAdminApproval' || !card.revealedCodes?.number ? (
            <>
              <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-6 gap-2">
                <div>
                  <h3 className="text-lg sm:text-2xl font-semibold text-gray-900 mb-1">
                    {card.brand || "Cadeaubon"}
                  </h3>
                  <p className="text-yellow-700 font-semibold text-sm">Wacht op admin verificatie</p>
                </div>
                <div className="sm:text-right">
                  <span className="block text-sm text-gray-400">Waarde: {formatPrice(card.originalValue)}</span>
                  <span className="block text-lg sm:text-xl font-bold text-yellow-700">Prijs cadeaubon: {formatPrice(card.sellingPrice)}</span>
                </div>
              </div>
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
                <p className="text-sm text-yellow-900 font-semibold">Verificatie loopt</p>
                <p className="text-sm text-yellow-800 mt-1">
                  Je betaling is ontvangen. De cadeauboncode wordt na verificatie per e-mail naar je verstuurd. Dit duurt doorgaans minder dan 24 uur.
                </p>
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col sm:flex-row sm:items-start justify-between mb-6 gap-2">
                <div>
                  <h3 className="text-lg sm:text-2xl font-semibold text-gray-900 mb-1">
                    {card.revealedCodes?.brand || "Cadeaubon"}
                  </h3>
                  <p className="text-orange-700 font-semibold text-sm">Bedankt voor je aankoop</p>
                </div>
                <div className="sm:text-right">
                  <span className="block text-sm text-gray-400">Waarde: {formatPrice(card.originalValue)}</span>
                  <span className="block text-lg sm:text-xl font-semibold text-orange-700">Prijs cadeaubon: {formatPrice(card.sellingPrice)}</span>
                </div>
              </div>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Cadeauboncode</label>
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <div className="flex-1 bg-gray-50 border-2 border-gray-200 rounded-xl p-3 sm:p-4">
                      <p className="text-base sm:text-2xl font-mono font-semibold text-gray-900 tracking-wider break-all">
                        {card.revealedCodes?.number}
                      </p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(card.revealedCodes?.number, `${card.id}-num`)}
                      className={`w-full sm:w-auto px-6 py-3 sm:py-4 rounded-xl font-semibold transition text-sm sm:text-base ${SECONDARY_BUTTON_CLASS}`}
                    >
                      {copied[`${card.id}-num`] ? '✓ Gekopieerd' : 'Kopieer code'}
                    </button>
                  </div>
                </div>

                {card.revealedCodes?.pin && (
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">PIN Code</label>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <div className="flex-1 bg-gray-50 border-2 border-gray-200 rounded-xl p-3 sm:p-4">
                        <p className="text-base sm:text-2xl font-mono font-semibold text-gray-900">
                          {card.revealedCodes.pin}
                        </p>
                      </div>
                      <button
                        onClick={() => copyToClipboard(card.revealedCodes.pin, `${card.id}-pin`)}
                        className={`w-full sm:w-auto px-6 py-3 sm:py-4 rounded-xl font-semibold transition text-sm sm:text-base ${SECONDARY_BUTTON_CLASS}`}
                      >
                        {copied[`${card.id}-pin`] ? '✓ Gekopieerd' : 'Kopieer PIN'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mt-6">
                <p className="text-sm text-orange-900 font-semibold">⏱️ 12-uurs garantie</p>
                <p className="text-xs text-orange-800 mt-1">
                  Werkt je code niet? Laat het ons binnen 12 uur weten via hello@swapify.nl. Als het nodig is, vergoeden we je aankoop volledig.
                </p>
              </div>
            </>
          )}
        </div>
      ))}
    
      {hasReleasedCodes && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 sm:p-6 mb-8 flex items-start sm:items-center space-x-3 sm:space-x-4">
          <div className="text-orange-600 text-2xl">📧</div>
          <p className="text-sm text-orange-900">
            We hebben je vrijgegeven codes ook naar je e-mailadres gestuurd. Check ook je spam/ongewenste e-mail.
          </p>
        </div>
      )}

      {hasPendingVerification && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 sm:p-6 mb-8 flex items-start sm:items-center space-x-3 sm:space-x-4">
          <div className="text-yellow-600 text-2xl">⏳</div>
          <p className="text-sm text-yellow-900">
            Voor cards die nog in verificatie staan, ontvang je automatisch een e-mail zodra de code is vrijgegeven.
          </p>
        </div>
      )}

      <Link to="/marketplace" className={`block text-center px-6 sm:px-8 py-3 sm:py-4 rounded-xl font-semibold text-sm sm:text-base transition ${PRIMARY_BUTTON_CLASS}`}>
        Terug naar marktplaats
      </Link>
    </div>
  );
};


const FailedPaymentPage = ({ onRetry }) => {
  const [cart, setCart] = useState({ items: [], expiresAt: null });
  const [summary, setSummary] = useState(null);
  const [releasing, setReleasing] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setCart(CartManager.cleanExpiredCart());
    try {
      setSummary(JSON.parse(localStorage.getItem('last_checkout_summary') || 'null'));
    } catch (storageError) {
      console.error('Could not parse checkout summary:', storageError);
    }
  }, []);

  const visibleItems = cart.items.length > 0 ? cart.items : (summary?.items || []);
  const subtotal = Number(summary?.subtotal || visibleItems.reduce((sum, item) => sum + Number(item.price || 0), 0));
  const checkoutFee = Number(summary?.checkoutFee || CHECKOUT_FEE);
  const total = Number(summary?.total || (subtotal + checkoutFee));
  const hasReservation = cart.items.length > 0;

  const handleRetry = () => {
    if (!hasReservation) {
      navigate('/marketplace');
      return;
    }

    onRetry({
      cart: cart.items,
      subtotal,
      checkoutFee,
      total,
      savings: 0,
      email: summary?.buyerEmail || getAuth().currentUser?.email || null,
      guestSessionId: summary?.guestSessionId || CartManager.getGuestSessionId(),
      orderId: `ord_${Date.now()}`,
      paymentAttemptNonce: createPaymentAttemptNonce('retry'),
    });
  };

  const handleCancelReservation = async () => {
    setReleasing(true);
    try {
      const functionsInstance = getFunctions(undefined, 'europe-west1');
      const releaseCard = httpsCallable(functionsInstance, 'releaseCard');
      const guestSessionId = summary?.guestSessionId || localStorage.getItem('swapify_guest_id');

      await Promise.all(cart.items.map((item) => releaseCard({
        cardId: item.id,
        ...(guestSessionId ? { guestSessionId } : {}),
      })));
    } catch (releaseError) {
      console.error('Could not release failed checkout reservation:', releaseError);
    } finally {
      CartManager.clearCart();
      localStorage.removeItem('last_mtr_uid');
      localStorage.removeItem('last_checkout_attempt_id');
      localStorage.removeItem('last_mtr_created_at');
      localStorage.removeItem('last_checkout_summary');
      navigate('/marketplace');
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="text-center mb-8">
        <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6 text-4xl">💳</div>
        <h1 className="text-3xl sm:text-4xl font-semibold text-gray-900 mb-3">Betaling niet gelukt</h1>
        <p className="text-gray-600 text-base sm:text-lg">
          Geen zorgen — er is niets afgeschreven. Je reservering staat nog even klaar, dus je kunt meteen opnieuw proberen.
        </p>
      </div>

      <div className={`${WARM_CARD_CLASS} p-5 sm:p-6 mb-6`}>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Je gereserveerde cadeaubonnen</h2>
        {visibleItems.length > 0 ? (
          <div className="space-y-3 text-sm sm:text-base text-gray-700">
            {visibleItems.map((item, index) => (
              <div key={item.cartId || item.id || index} className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-semibold text-gray-900">{item.brand}</p>
                  <p className="text-sm text-gray-500">Waarde {formatPrice(item.value || item.cardValue || item.originalValue || 0)}</p>
                </div>
                <span className="font-semibold text-gray-900 whitespace-nowrap">{formatPrice(item.price || item.sellingPrice || 0)}</span>
              </div>
            ))}
            <div className="border-t border-gray-200 pt-3 space-y-2">
              <div className="flex justify-between"><span>Subtotaal</span><span>{formatPrice(subtotal)}</span></div>
              <div className="flex justify-between"><span>iDEAL kosten</span><span>{formatPrice(checkoutFee)}</span></div>
              <div className="flex justify-between font-semibold text-gray-900"><span>Totaal</span><span>{formatPrice(total)}</span></div>
            </div>
          </div>
        ) : (
          <p className="text-gray-600">Je reservering is verlopen. Kies gerust een nieuwe kaart uit.</p>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={handleRetry}
          disabled={!hasReservation || releasing}
          className={`flex-1 py-4 px-6 rounded-xl font-bold transition ${hasReservation && !releasing ? PRIMARY_BUTTON_CLASS : PRIMARY_BUTTON_DISABLED}`}
        >
          Probeer opnieuw
        </button>
        <button
          type="button"
          onClick={handleCancelReservation}
          disabled={releasing}
          className={`flex-1 py-4 px-6 rounded-xl font-bold transition ${SECONDARY_BUTTON_CLASS} disabled:opacity-50`}
        >
          {releasing ? 'Even vrijgeven...' : 'Annuleer en kies een andere kaart'}
        </button>
      </div>

      <p className="text-xs text-gray-500 mt-4 text-center">
        Je reservering verloopt automatisch als je niets doet.
      </p>
    </div>
  );
};

// ============================================================================
// MAIN BUY PAGE COMPONENT
// ============================================================================
const BuyPage = () => {
  const location = useLocation();

  // 1. Determine the initial step BEFORE the component even renders
  const getStepFromLocation = (targetLocation = window.location) => {
    const params = new URLSearchParams(targetLocation.search);
    const pathname = targetLocation.pathname; // Get the current URL path

    if (pathname.includes('/buy/return')) return 5;
    if (pathname.includes('/buy/failed')) return 4;

    const isSuccess = params.get('step') === 'success' || 
                      params.get('payment_id') || 
                      params.get('uid') || 
                      params.get('multi_transaction_uid');
  
  // ✅ FIX: Check if the parameter is success OR if the URL path is /order/success
  const isSuccessPath = pathname.includes('/order/success');
    
    return (isSuccess || isSuccessPath) ? 3 : 1;
  };

// 2. Initialize state with our calculation
  const [currentStep, setCurrentStep] = useState(getStepFromLocation());
  const [orderData, setOrderData] = useState({});

  // 3. Keep local step state in sync with browser navigation.
  useEffect(() => {
    setCurrentStep(getStepFromLocation(location));
  }, [location]);

  const handleCartNext = (data) => {
    setOrderData(data);
    setCurrentStep(2);
  };
  
  const handleBack = () => setCurrentStep(1);

  const handleFailedRetry = (data) => {
    setOrderData(data);
    window.history.replaceState(null, '', '/buy');
    setCurrentStep(2);
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 py-12">
      {/* Step Indicator */}
      {currentStep < 3 && (
        <div className="max-w-4xl mx-auto px-4 mb-12">
          <div className="flex items-center justify-center space-x-4">
            {[1, 2].map((step) => (
              <React.Fragment key={step}>
                <div className={`flex items-center justify-center w-12 h-12 rounded-full font-semibold transition ${
                  step === currentStep
                    ? 'bg-gradient-to-r from-red-500 to-orange-500 text-white shadow-lg'
                    : step < currentStep
                    ? 'bg-orange-100 text-orange-700'
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {step < currentStep ? '✓' : step}
                </div>
                {step < 2 && (
                  <div className={`w-24 h-1 ${step < currentStep ? 'bg-orange-500' : 'bg-gray-300'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="flex justify-center space-x-28 mt-3">
            <span className="text-sm font-semibold text-gray-700">Winkelwagen</span>
            <span className="text-sm font-semibold text-gray-700">Betaling</span>
          </div>
        </div>
      )}
      
      {/* Step Routing */}
      <div className="transition-all duration-300">
        {currentStep === 1 && <CartStep onNext={handleCartNext} />}
        {currentStep === 2 && <PaymentStep cartData={orderData} onBack={handleBack} />}
        {currentStep === 3 && <SuccessStep />}
        {currentStep === 4 && <FailedPaymentPage onRetry={handleFailedRetry} />}
        {currentStep === 5 && <PaymentReturnStep />}
      </div>

      <div className="mt-16">
        <SiteFooter />
      </div>
    </div>
  );
};

export { BuyPage, CartManager };
export default BuyPage;
